import React, { useState, createContext, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext({
  isLoggedIn: false,
  user: null,
  login: () => {},
  logout: () => {},
  setUser: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  let navigate = useNavigate();

  // 초기 상태를 localStorage/sessionStorage에서 확인
  const getInitialLoginState = () => {
    try {
      const storedUser = sessionStorage.getItem("user") || localStorage.getItem("user");
      console.log('🔄 Initial login state check:', storedUser ? 'found user' : 'no user');
      return !!storedUser;
    } catch (error) {
      console.log('⚠️ Error checking initial login state:', error);
      return false;
    }
  };

  const getInitialUser = () => {
    try {
      const storedUser = sessionStorage.getItem("user") || localStorage.getItem("user");
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        console.log('🔄 Initial user loaded:', userData?.username);
        return userData;
      }
    } catch (error) {
      console.log('⚠️ Error parsing initial user:', error);
    }
    return null;
  };

  const [isLoggedIn, setIsLoggedIn] = useState(getInitialLoginState); 
  const [user, setUser] = useState(getInitialUser); 
  const [loading, setLoading] = useState(true);

  console.log('🔄 AuthProvider rendered with state:', { isLoggedIn, user: user?.username, loading });

  // 세션 만료 시간 설정 (7일)
  const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000; // 7일을 밀리초로

  // 세션 유효성 검사
  const isSessionValid = (loginTime) => {
    if (!loginTime) return false;
    const currentTime = new Date().getTime();
    return (currentTime - loginTime) < SESSION_DURATION;
  };

  // 사용자 활동 시 세션 연장
  const updateActivity = () => {
    if (isLoggedIn) {
      const sessionData = localStorage.getItem("sessionData");
      if (sessionData) {
        try {
          const parsedSessionData = JSON.parse(sessionData);
          const updatedSessionData = {
            ...parsedSessionData,
            lastActivity: new Date().getTime()
          };
          localStorage.setItem("sessionData", JSON.stringify(updatedSessionData));
        } catch (e) {
          console.log('⚠️ Failed to update activity time');
        }
      }
    }
  };

  // 사용자 활동 감지를 위한 이벤트 리스너
  useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    // 모든 활동 이벤트에 리스너 추가
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // 주기적으로 세션 상태 확인 (10분마다)
    const sessionCheckInterval = setInterval(() => {
      const sessionData = localStorage.getItem("sessionData");
      if (sessionData && isLoggedIn) {
        try {
          const parsedSessionData = JSON.parse(sessionData);
          if (!isSessionValid(parsedSessionData.loginTime)) {
            console.log('⏰ Session expired during activity check');
            logout();
          }
        } catch (e) {
          console.log('⚠️ Error during session check');
        }
      }
    }, 10 * 60 * 1000); // 10분

    // 클린업
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearInterval(sessionCheckInterval);
    };
  }, [isLoggedIn]);

  // 컴포넌트 마운트 시 세션 스토리지에서 사용자 정보 확인
  useEffect(() => {
    let isMounted = true; // 컴포넌트 마운트 상태 추적
    
    const checkLoginStatus = async () => {
      console.log('🔍 AuthContext: 로그인 상태 확인 시작');
      console.log('🔍 AuthContext: Current state before check:', { isLoggedIn, user: user?.username });
      
      try {
        // 먼저 sessionStorage 확인 (현재 세션)
        let storedUser = sessionStorage.getItem("user");
        console.log('📱 SessionStorage user:', storedUser ? 'found' : 'not found');
        
        // sessionStorage에 없으면 localStorage 확인 (이전 세션)
        if (!storedUser) {
          storedUser = localStorage.getItem("user");
          console.log('💾 LocalStorage user:', storedUser ? 'found' : 'not found');
          // localStorage에서 복원된 경우 sessionStorage에도 저장
          if (storedUser) {
            sessionStorage.setItem("user", storedUser);
            console.log('📱 Restored user to sessionStorage');
          }
        }
        
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          console.log('👤 Parsed user data:', userData);
          
          // 로컬 세션 시간 확인
          const sessionData = localStorage.getItem("sessionData");
          let loginTime = null;
          
          if (sessionData) {
            try {
              const parsedSessionData = JSON.parse(sessionData);
              loginTime = parsedSessionData.loginTime;
            } catch (e) {
              console.log('⚠️ Invalid session data format');
            }
          }
          
          // 세션 시간 유효성 검사
          if (!isSessionValid(loginTime)) {
            console.log('⏰ Local session expired, clearing storage');
            localStorage.removeItem("user");
            localStorage.removeItem("sessionData");
            sessionStorage.removeItem("user");
            if (isMounted) {
              setUser(null);
              setIsLoggedIn(false);
            }
            return;
          }
          
          console.log('✅ Local session valid, updating last activity');
          // 마지막 활동 시간 업데이트
          const updatedSessionData = {
            loginTime: loginTime,
            lastActivity: new Date().getTime()
          };
          localStorage.setItem("sessionData", JSON.stringify(updatedSessionData));
          
          // 서버 세션 확인
          console.log('🌐 Checking server session...');
          try {
            const response = await fetch('/auth/check-session', {
              method: 'GET',
              credentials: 'include'
            });
            console.log('🌐 Session check response status:', response.status);
            
            if (response.status === 401) {
              console.log('❌ Server session expired (401), clearing local storage');
              localStorage.removeItem("user");
              sessionStorage.removeItem("user");
              if (isMounted) {
                setUser(null);
                setIsLoggedIn(false);
              }
              return;
            }
            
            const sessionResult = await response.json();
            console.log('🌐 Session check result:', sessionResult);
            
            if (sessionResult.success && sessionResult.user) {
              console.log('✅ Server session valid, user logged in');
              if (isMounted) {
                setUser(sessionResult.user);
                setIsLoggedIn(true);
                console.log('✅ AuthContext: State updated to logged in');
              }
            } else {
              console.log('❌ Server session invalid, clearing local storage');
              localStorage.removeItem("user");
              sessionStorage.removeItem("user");
              if (isMounted) {
                setUser(null);
                setIsLoggedIn(false);
              }
            }
          } catch (sessionError) {
            console.error('🌐 Session check failed:', sessionError);
            // 네트워크 오류 등의 경우 로컬 데이터 사용 (하지만 서버 검증은 필요시에만)
            console.log('⚠️ Using local user data due to session check failure');
            if (isMounted) {
              setUser(userData);
              setIsLoggedIn(true);
              console.log('⚠️ AuthContext: State updated using local data');
            }
          }
        } else {
          console.log('❌ No stored user found');
          if (isMounted) {
            setUser(null);
            setIsLoggedIn(false);
          }
        }
      } catch (error) {
        console.error('💥 로그인 상태 확인 중 오류 발생:', error);
        localStorage.removeItem("user");
        sessionStorage.removeItem("user");
        if (isMounted) {
          setUser(null);
          setIsLoggedIn(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
        console.log('🔍 AuthContext: 로그인 상태 확인 완료');
      }
    };
    
    checkLoginStatus();
    
    // 클린업 함수
    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (userData) => {
    console.log('🔐 AuthContext: Login called with userData:', userData);
    
    const currentTime = new Date().getTime();
    
    // 사용자 데이터 저장
    const userDataString = JSON.stringify(userData);
    localStorage.setItem("user", userDataString);
    sessionStorage.setItem("user", userDataString);
    
    // 세션 데이터 저장 (로그인 시간과 마지막 활동 시간)
    const sessionData = {
      loginTime: currentTime,
      lastActivity: currentTime
    };
    localStorage.setItem("sessionData", JSON.stringify(sessionData));
    
    console.log('💾 User data and session data saved to localStorage and sessionStorage');
    console.log('⏰ Session will expire in', SESSION_DURATION / (24 * 60 * 60 * 1000), 'days');
    
    setIsLoggedIn(true);
    setUser(userData);
    console.log('✅ AuthContext: Login state updated');
  };
  
  const logout = async () => {
    // 모든 저장소에서 제거
    localStorage.removeItem("user");
    localStorage.removeItem("sessionData");
    sessionStorage.removeItem("user");
    setIsLoggedIn(false);
    setUser(null);
    console.log('👋 User logged out, all storage cleared');
    navigate("/");
  };

  const updateUserProfile = async (profileData) => {
    try {
      // 직접 user 상태 업데이트 (Settings에서 이미 DB에 저장됨)
      const updatedUser = { ...user, ...profileData };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      sessionStorage.setItem("user", JSON.stringify(updatedUser));

      console.log('✅ User profile updated in AuthContext:', updatedUser);

      return {
        success: true,
        user: updatedUser
      };
    } catch (error) {
      console.error('프로필 업데이트 오류:', error);
      return {
        success: false,
        message: '프로필 업데이트 중 오류가 발생했습니다.'
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{ 
        isLoggedIn, 
        user, 
        login, 
        logout, 
        updateUserProfile, 
        updateActivity,
        setIsLoggedIn, 
        setUser, 
        loading 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};